import classNames from 'classnames';
import DropdownArrow from 'public/static/images/icons/Dropdown_arrow.svg';
import DropdownArrowBlue from 'public/static/images/icons/icon_dropdown-arrow_full_blue.svg';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import styles from './Dropdown.module.scss';
import {
  DropdownItem,
  DropdownMods,
  DropdownProps,
  DropdownValue,
} from './Dropdown.types';

function prepareValue<Values extends DropdownValue>(
  item: DropdownItem<Values> | Values,
) {
  const isValue = typeof item === 'string';

  return {
    text: isValue ? item : item.text,
    value: isValue ? item : item.value,
    disabled: isValue ? false : item.disabled,
    placeholder: isValue ? false : item.placeholder,
  };
}

export function Dropdown<Values extends DropdownValue>({
  value,
  values,
  onBlur = () => value,
  onChange = () => value,
  className,
  disabled,
  mods,
  label,
  listMaxHeight,
}: DropdownProps<Values>) {
  const [touched, setTouched] = useState(false);
  const [opened, setOpened] = useState(false);
  const current = useMemo(() => {
    const dropdownValues = values.map(prepareValue);

    return (
      dropdownValues.find((item) => item.value === value) || dropdownValues[0]
    );
  }, [value, values]);

  function handleSelectChange({ target }: ChangeEvent<HTMLSelectElement>) {
    onChange(target.value as Values);
  }

  function handleOptionClick(optionValue: Values) {
    onChange(optionValue);
    setOpened(false);
  }

  function handleToggleClick() {
    if (!disabled) {
      setOpened(!opened);
    }
  }

  useEffect(() => {
    const handleClick = function () {
      setOpened(false);
    };

    document.addEventListener('click', handleClick);

    return function () {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  useEffect(() => {
    if (touched && !opened) {
      onBlur();
    }
  }, [opened]);

  return (
    <fieldset
      onClick={(event) => {
        event.stopPropagation();
        setTouched(true);
      }}
      disabled={disabled}
      className={classNames(
        styles.wrapper,
        className,
        {
          [styles.opened]: opened,
          [styles.disabled]: disabled,
        },
        mods?.map((mod) => styles[mod]),
      )}
    >
      {label && <div className={styles.label}>{label}</div>}
      <div
        className={styles.head}
        onClick={handleToggleClick}
        title={current.text || current.value}
      >
        <div
          className={classNames(styles.value, {
            [styles.placeholder]: current.placeholder,
          })}
        >
          <span>{current.text || current.value}</span>
        </div>
        <div className={styles.dropdownIcon}>
          {mods?.includes(DropdownMods.ArrowBlue) ? (
            <DropdownArrowBlue />
          ) : (
            <DropdownArrow />
          )}
        </div>
      </div>
      {opened && !disabled && (
        <div className={styles.dropdown}>
          <div
            style={{ maxHeight: `${listMaxHeight}px` }}
            className={styles.innerDropdown}
          >
            {values.map((item) => {
              const dropdownItem = prepareValue<Values>(item);

              return (
                <button
                  title={dropdownItem.text || dropdownItem.value}
                  key={dropdownItem.value}
                  className={classNames(styles.option, {
                    [styles.optionActive]: value === dropdownItem.value,
                    [styles.placeholder]: dropdownItem.placeholder,
                  })}
                  disabled={dropdownItem.disabled}
                  onClick={() => handleOptionClick(dropdownItem.value)}
                >
                  {dropdownItem.text || dropdownItem.value}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {!disabled && (
        <select
          value={value}
          onChange={handleSelectChange}
          disabled={disabled}
          className={styles.select}
        >
          {values.map((item) => {
            const dropdownItem = prepareValue<Values>(item);

            return (
              <option
                key={dropdownItem.value}
                value={dropdownItem.value}
                disabled={dropdownItem.disabled}
              >
                {dropdownItem.text || dropdownItem.value}
              </option>
            );
          })}
        </select>
      )}
    </fieldset>
  );
}
